'use client';

import { ApiKeys, fetcher } from '@insta-club/api';
import { SendIcon } from '@insta-club/components/icons';
import SendFillIcon from '@insta-club/components/icons/send-fill';
import { useCustomForm } from '@insta-club/hooks';
import { ChatBotMessageResponse } from '@insta-club/models';
import { Form } from '@insta-club/ui';
import { AssistantComposerFormValues, AssistantComposerSchema } from '@insta-club/validator';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import toast from 'react-hot-toast';

interface Props {
    id: string;
    date?: string;
    disabled: boolean;
    beforeSubmit?: () => void;
    onSuccess?: (message: ChatBotMessageResponse) => void;
}

const AssistantMessageComposer: React.FC<Props> = ({ id, date, onSuccess, beforeSubmit, disabled }) => {
    const form = useCustomForm(AssistantComposerSchema);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getFetcher = (msg: string) => {
        if (date) {
            return fetcher(ApiKeys.POST_CHATBOTS_STORY_MSG(id), 'dashboard', 'POST', {
                body: { msg },
                params: { date_string: date }
            });
        } else {
            return fetcher(ApiKeys.POST_CHATBOTS_MSG(id), 'dashboard', 'POST', {
                body: { msg }
            });
        }
    };

    const onSubmit: SubmitHandler<AssistantComposerFormValues> = async ({ msg }) => {
        if (isSubmitting) return;

        try {
            setIsSubmitting(true);
            beforeSubmit?.();
            const res: ChatBotMessageResponse = await getFetcher(msg);
            onSuccess?.(res);
            form.reset();
        } catch (err: unknown) {
            form.reset();
            toast.removeAll();
            if (err instanceof Error) {
                toast.error('خطای ناشناخته رخ داده است');
            } else {
                const error = err as { status: number };
                if (error.status && error.status == 402) {
                    toast.error('محدودیت روزانه استفاده از توکن دستیار هوشمند برای پیج شما به پایان رسیده است.');
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const isDisabled = disabled || isSubmitting;

    return (
        <div className='shadow-card absolute inset-x-0 bottom-0 z-40 w-full bg-white p-4'>
            <Form form={form} onSubmit={onSubmit}>
                <div className='flex items-center space-x-4'>
                    <input
                        type='text'
                        autoComplete='off'
                        placeholder='اینجا بنویسید'
                        disabled={isDisabled}
                        {...form.register('msg')}
                        className='h-10 w-full flex-1 rounded-md border-none bg-gray-100 px-4 text-sm outline-none placeholder:text-sm disabled:opacity-50'
                    />
                    <button
                        disabled={isDisabled || !form.formState.isValid}
                        type='submit'
                        className='text-primary-500 rotate-180 cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400'
                    >
                        {form.formState.isValid && !isSubmitting ? (
                            <SendFillIcon width={28} height={28} />
                        ) : (
                            <SendIcon width={28} height={28} />
                        )}
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default AssistantMessageComposer;

